import React from 'react';
import {addHours, format} from 'date-fns';
import {calcularValorFinalBoleto} from '../../../../helpers/calculadoraValorBoleto';

/**
 * Formata um número para o formato de moeda brasileira.
 * @param {number} valor
 * @returns {string}
 */
function formatarBRL(valor) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(valor || 0);
}

/**
 * Componente para exibir o resumo da nova segunda via de boleto.
 *
 * @param {Object} props
 * @param {string} props.descricao - Descrição do boleto.
 * @param {Date|string} props.novoVencimento - Nova data de vencimento.
 * @param {number} props.valorBase - Valor original do boleto.
 * @param {number|null} props.novoValor - Novo valor (se houver).
 * @param {number|null} props.novoDesconto - Desconto aplicado.
 * @param {boolean} props.aplicarMulta - Indica se deve aplicar multa.
 * @param {boolean} props.aplicarJuros - Indica se deve aplicar juros.
 * @param {number} props.multaPercentual - Percentual de multa.
 * @param {number} props.jurosPercentual - Percentual de juros ao mês.
 * @param {string|Date} props.vencimentoOriginal - Vencimento anterior para cálculo dos dias.
 */
export default function PreviewBoletoConfirmacao({
                                                   descricao,
                                                   novoVencimento,
                                                   valorBase,
                                                   novoValor,
                                                   novoDesconto,
                                                   aplicarMulta,
                                                   aplicarJuros,
                                                   multaPercentual,
                                                   jurosPercentual,
                                                   vencimentoOriginal,
                                                 }) {
  const diasVencido = calcularDiasVencido(vencimentoOriginal, novoVencimento);

  const valorFinal = calcularValorFinalBoleto({
    valor_base: valorBase,
    novo_valor: novoValor,
    novo_desconto: novoDesconto,
    aplicar_juros: aplicarJuros,
    aplicar_multa: aplicarMulta,
    dias_vencido: diasVencido,
    multa_percentual: multaPercentual,
    juros_percentual: jurosPercentual,
  });

  return (
    <div className="flex flex-col gap-2 w-full">
      <div>
        <span className="font-bold">Descrição:</span> {descricao}
      </div>
      <div>
        <span className="font-bold">Novo vencimento:</span> {format(new Date(novoVencimento), 'dd/MM/yyyy')}
      </div>
      <div>
        <span className="font-bold">Valor original:</span> {formatarBRL(valorBase)}
      </div>
      <div>
        <span className="font-bold">Novo valor:</span> {formatarBRL(novoValor ?? valorBase)}
      </div>
      <div>
        <span className="font-bold">Desconto:</span> {formatarBRL(novoDesconto)}
      </div>
      <div>
        <span className="font-bold">Aplicar Multa:</span> {aplicarMulta ? 'Sim' : 'Não'}
      </div>
      <div>
        <span className="font-bold">Aplicar Juros:</span> {aplicarJuros ? 'Sim' : 'Não'}
      </div>
      <div className="text-lg mt-2">
        <span className="font-bold text-green-700">Valor Final:</span>{' '}
        <b>{formatarBRL(valorFinal)}</b>
      </div>
    </div>
  );
}

/**
 * Calcula a quantidade de dias entre duas datas.
 * @param {string|Date} dataAnterior
 * @param {string|Date} dataNova
 * @returns {number}
 */
function calcularDiasVencido(dataAnterior, dataNova) {
  const d1 = addHours(new Date(dataAnterior), 3);
  const d2 = new Date(dataNova);
  const diff = Math.ceil((d2 - d1) / (1000 * 3600 * 24));
  return diff > 0 ? diff : 0;
}
